import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActionSheetController, LoadingController, ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { SiblingStudent } from '../../models/sibling.model';
import { SiblingService } from '../../services/sibling.service';

@Component({
  selector: 'app-sibling-switcher',
  templateUrl: './sibling-switcher.component.html',
  styleUrls: ['./sibling-switcher.component.scss'],
})
export class SiblingSwitcherComponent implements OnInit, OnDestroy {
  @Input() compact = false;
  @Input() variant: 'menu' | 'light' = 'menu';
  @Output() switched = new EventEmitter<SiblingStudent>();

  siblings: SiblingStudent[] = [];
  currentSibling?: SiblingStudent;
  private sub?: Subscription;

  constructor(
    private siblingService: SiblingService,
    private actionSheetController: ActionSheetController,
    private loadingController: LoadingController,
    private toastController: ToastController
  ) {}

  ngOnInit(): void {
    this.sub = this.siblingService.siblings$.subscribe((list) => {
      this.siblings = list;
      this.currentSibling = list.find((s) => s.is_current);
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get showSwitcher(): boolean {
    return this.siblings.length > 1;
  }

  async openSwitcher(): Promise<void> {
    if (!this.showSwitcher) {
      return;
    }

    const sheet = await this.actionSheetController.create({
      header: 'Switch child',
      subHeader: 'View updates for another student',
      buttons: [
        ...this.siblings.map((sibling) => ({
          text: this.formatSiblingLabel(sibling),
          icon: sibling.is_current ? 'checkmark-circle' : 'person-outline',
          handler: () => {
            if (!sibling.is_current) {
              this.performSwitch(sibling);
            }
          },
        })),
        { text: 'Cancel', role: 'cancel' },
      ],
    });

    await sheet.present();
  }

  private formatSiblingLabel(sibling: SiblingStudent): string {
    const parts = [sibling.full_name];
    if (sibling.class_section) {
      parts.push(sibling.class_section);
    }
    if (sibling.roll_number) {
      parts.push(`Roll ${sibling.roll_number}`);
    }
    return parts.join(' · ');
  }

  private async performSwitch(sibling: SiblingStudent): Promise<void> {
    const loading = await this.loadingController.create({
      message: 'Switching student...',
    });
    await loading.present();

    this.siblingService.switchSibling(sibling.id).subscribe({
      next: () => {
        loading.dismiss();
        this.switched.emit(sibling);
        window.location.href = '/home';
      },
      error: async () => {
        loading.dismiss();
        const toast = await this.toastController.create({
          message: 'Could not switch student. Please try again.',
          duration: 2500,
          color: 'danger',
        });
        await toast.present();
      },
    });
  }
}
